import type { Assignee, TaskStatus } from "./domain";

export interface PlanAtomicTemplate {
  title: string;
  assignee: Assignee;
  status: TaskStatus;
  modelUsed?: string;
  creditsCharged?: number;
  completedAtNow?: boolean;
}

export interface PlanSubtaskTemplate {
  title: string;
  assignee: Assignee;
  status: TaskStatus;
  atomicTasks: PlanAtomicTemplate[];
}

export interface TaskPlanTemplate {
  profile: "general";
  subtasks: PlanSubtaskTemplate[];
}

interface PlanningInput {
  title: string;
  description?: string;
  assignee: Assignee;
}

const AGENT_ASSIGNEE: Assignee = { type: "agent", agentId: "agent_code" };
const MEMBER_ASSIGNEE: Assignee = { type: "member", userId: "user_1", role: "member" };

const withTaskTitle = (prefix: string, title: string): string => `${prefix} for ${title.trim() || "this task"}`;
const isResearchRankingTask = (title: string): boolean => /(list top|top \d+|best|compare|comparison|rank|ranking|shortlist|evaluate)/i.test(title);
const isBillPaymentTask = (context: string): boolean =>
  /(pay|payment).*(electricity|utility|water|gas|internet|phone|bill)|\b(electricity bill|bill payment|amazon pay|upi)\b/i.test(context);
const isEmailDeliveryTask = (context: string): boolean =>
  /\b(?:send|forward)\b[\s\S]*\b(?:email|message)\b/i.test(context) ||
  /\bemail\b[\s\S]*\bto\b[\s\S]*[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9.-]+\.[a-z]{2,}\b/i.test(context);
const isBuyingGuideTask = (context: string): boolean =>
  /\bbuying guide\b|\bbuyer'?s guide\b|\bbifl\b|\bcategory page\b|\bproduct guide\b|\bsoundbar\b|\bcomparison guide\b/i.test(context);
const isInvestmentNoteTask = (context: string): boolean =>
  /\binvestment note\b|\binvestment memo\b|\bjp morgan\b|\bjpmorgan\b|\bmarket note\b|\betf\b|\bgoldbees\b|\bmutual fund\b|\bportfolio\b/i.test(context);
const isDraftOnlyInvestmentNoteTask = (context: string): boolean =>
  /\bscope_done\s*:\s*wordpress draft\b|\bunpublished wordpress (?:post )?draft\b|\bcreate an unpublished wordpress draft\b/i.test(context) &&
  !/\bscope_done\s*:\s*(?:published|publish-ready)\b/i.test(context);
export const isDraftOnlyWordpressInvestmentNote = (input: { title: string; description?: string }): boolean => {
  const context = `${input.title}\n${input.description ?? ''}`;
  return isInvestmentNoteTask(context) && isDraftOnlyInvestmentNoteTask(context);
};
const isWordpressContentTask = (context: string): boolean =>
  /\bwordpress\b|\bwp[-\s]?blog\b|\bblog post\b|\bpublish\b|\bdraft\b|\bcms\b/i.test(context) && isContentGuideTask(context);
const isConcreteSiteAutomationTask = (context: string): boolean =>
  /https?:\/\/[^\s"'`<>]+|\b([a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+)\b/i.test(context) &&
  /\b(add|create|insert|update|edit|modify|delete|remove|upsert|save|publish)\b/i.test(context) &&
  /\b(category|categories|collection|database|db|table|document|record|cms|admin|panel)\b/i.test(context);
const isContentGuideTask = (context: string): boolean =>
  /\bguide\b|\barticle\b|\bblog\b|\bcopy\b|\bcontent\b|\blanding page\b|\bcategory page\b|\bproduct page\b/i.test(context);

const detectGuideSubject = (title: string, context: string): string => {
  const joined = `${title} ${context}`.replace(/\s+/g, " ").trim();
  const directMatch = joined.match(/(?:buying guide|buyer'?s guide|guide)\s+(?:for|on)\s+([a-z0-9][a-z0-9\s-]{2,48})/i)?.[1];
  const topMatch = joined.match(/top\s+\d+\s+([a-z0-9][a-z0-9\s-]{2,48})/i)?.[1];
  const source = (directMatch ?? topMatch ?? "").replace(/\b(with|and|which|that)\b.*$/i, "").trim();
  return source.length > 0 ? source : "target products";
};

const detectInvestmentSubject = (title: string, context: string): string => {
  const joined = `${title} ${context}`.replace(/\s+/g, " ").trim();
  const explicitMatch = joined.match(/\b(GOLDBEES|[A-Z0-9]{3,}\s*ETF|[A-Z0-9]{3,}\s*FUND)\b/)?.[1];
  if (explicitMatch) return explicitMatch.replace(/\s+/g, " ").trim();
  const aboutMatch = joined.match(/(?:about|on|for)\s+([a-z0-9][a-z0-9\s-]{2,40})(?:\s+in\s+|\s+as\s+|$)/i)?.[1];
  const subject = (aboutMatch ?? "").replace(/\b(blog|post|investment|note|style)\b/gi, "").replace(/\s+/g, " ").trim();
  return subject.length > 0 ? subject : "target investment topic";
};

const attachStatuses = (
  assignee: Assignee,
  definitions: Array<{ title: string; atomic: Array<string | { title: string; assignee?: "agent" | "member" | "auto" }> }>
): PlanSubtaskTemplate[] => {
  const executionAssignee = assignee.type === "agent" ? assignee : AGENT_ASSIGNEE;
  const reviewAssignee = assignee.type === "member" ? assignee : executionAssignee;
  const autoAssignee: Assignee = assignee.type === "auto" ? assignee : { type: "auto", strategy: "skill_match" };

  return definitions.map((definition, subtaskIndex) => ({
    title: definition.title,
    assignee,
    status: "pending",
    atomicTasks: definition.atomic.map((atomicEntry) => {
      const atomicTitle = typeof atomicEntry === "string" ? atomicEntry : atomicEntry.title;
      const hintedAssignee = typeof atomicEntry === "string" ? undefined : atomicEntry.assignee;
      const atomicAssignee =
        hintedAssignee === "member"
          ? MEMBER_ASSIGNEE
          : hintedAssignee === "auto"
            ? autoAssignee
            : hintedAssignee === "agent"
              ? executionAssignee
              : subtaskIndex === 2
                ? reviewAssignee
                : executionAssignee;
      const atomicStatus: TaskStatus = "pending";
      return {
        title: atomicTitle,
        assignee: atomicAssignee,
        status: atomicStatus,
        modelUsed: atomicAssignee.type === "member" ? undefined : "openai/gpt-4o-mini"
      };
    })
  }));
};

const fallbackPlan = (title: string, context: string): Array<{ title: string; atomic: Array<string | { title: string; assignee?: "agent" | "member" | "auto" }> }> => {
  if (isConcreteSiteAutomationTask(context)) {
    return [
      {
        title: "Open target admin surface and capture baseline",
        atomic: [
          "Navigate to the target site admin or collection interface",
          "Capture baseline state before applying changes"
        ]
      },
      {
        title: withTaskTitle("Apply requested site or data mutation", title),
        atomic: [
          "Start a new entry or open the relevant edit flow",
          `Populate fields exactly per task: "${title}"`,
          "Save or publish the update and wait for confirmation"
        ]
      },
      {
        title: withTaskTitle("Validate result and assemble execution evidence", title),
        atomic: [
          "Refresh and verify the requested item is present",
          "Summarize before/after proof and any follow-up checks"
        ]
      }
    ];
  }

  if (isBillPaymentTask(context)) {
    return [
      {
        title: withTaskTitle("Fetch bill details and verify payable amount", title),
        atomic: [
          { title: "Open Amazon Pay electricity bill flow and retrieve the latest Gurgaon home bill", assignee: "member" },
          { title: "Confirm consumer account, payable amount, and due date from the bill details screen", assignee: "member" }
        ]
      },
      {
        title: withTaskTitle("Execute payment before deadline", title),
        atomic: [
          { title: "Complete bill payment on Amazon Pay using your selected payment method before due date", assignee: "member" },
          { title: "Capture transaction ID, paid amount, and payment timestamp from confirmation screen", assignee: "member" }
        ]
      },
      {
        title: withTaskTitle("Verify settlement and archive proof", title),
        atomic: [
          { title: "Check bill status is marked paid in provider/app payment history", assignee: "member" },
          { title: "Upload payment receipt evidence and transaction reference to task activity", assignee: "member" }
        ]
      }
    ];
  }

  if (isEmailDeliveryTask(context)) {
    return [
      {
        title: withTaskTitle("Prepare the requested email deliverable", title),
        atomic: [
          "Retrieve and verify the final reviewed source-task output",
          "Compose the email body and attachments without sending"
        ]
      },
      {
        title: withTaskTitle("Validate and approve the email", title),
        atomic: [
          "Validate recipient, subject, body, and attachments against task constraints",
          { title: "Review and approve the email draft and recipient before sending", assignee: "member" }
        ]
      },
      {
        title: withTaskTitle("Send the approved email and capture proof", title),
        atomic: [
          "Send the approved email to the specified recipient and capture provider confirmation",
          "Verify the sent-message record and publish the message ID with a concise handoff"
        ]
      }
    ];
  }

  if (isBuyingGuideTask(context)) {
    const subject = detectGuideSubject(title, context);
    return [
      {
        title: withTaskTitle("Define buying guide scope and scoring rubric", title),
        atomic: [
          `Extract audience intent, price bands, and must-have criteria for ${subject}`,
          "Define evaluation rubric (durability, performance, support, value)",
          "Lock output structure: intro, comparison table, recommendations, FAQs"
        ]
      },
      {
        title: withTaskTitle("Research and shortlist credible options", title),
        atomic: [
          `Collect candidate ${subject} options from trusted sources with links`,
          "Capture specs, warranty/support details, and long-term reliability signals",
          "Filter and rank candidates against the rubric"
        ]
      },
      {
        title: withTaskTitle("Draft category-page-ready buying guide", title),
        atomic: [
          "Write recommendation sections with concise rationale for each shortlisted option",
          "Build comparison table with key specs, pros/cons, and best-for tags",
          "Attach source-backed claims and supporting references"
        ]
      },
      {
        title: withTaskTitle("Validate, publish handoff, and archive artifacts", title),
        atomic: [
          "Run factual and link validation checks across all recommendations",
          "Apply final readability and SEO intent edits",
          "Upload final guide artifact to requested destination and share handoff summary"
        ]
      }
    ];
  }

  if (isInvestmentNoteTask(context)) {
    const subject = detectInvestmentSubject(title, context);
    if (isDraftOnlyWordpressInvestmentNote({ title, description: context })) {
      return [
        {
          title: withTaskTitle(`Research and write the complete ${subject} investment note`, title),
          atomic: [
            `Build a concise, dated primary-source evidence pack for ${subject}`,
            `Write the complete WordPress-ready institutional investment note for ${subject} with title, excerpt, SEO metadata, adjacent citations, risks, and disclosures`
          ]
        },
        {
          title: withTaskTitle(`Audit and create the unpublished ${subject} WordPress draft`, title),
          atomic: [
            `Audit and correct the complete ${subject} note in place, returning the full final note and metadata`,
            `Create an unpublished WordPress post draft from the audited ${subject} note, then read it back, verify citation links and post_status=draft, and capture preview proof`
          ]
        }
      ];
    }
    return [
      {
        title: withTaskTitle(`Define ${subject} investment-note brief and WordPress outcome`, title),
        atomic: [
          `Lock audience, thesis, and WordPress draft/publish target for ${subject}`,
          "Interpret JP Morgan style as professional investment-note structure without copying proprietary wording"
        ]
      },
      {
        title: withTaskTitle(`Gather source evidence and risk context for ${subject}`, title),
        atomic: [
          `Collect current company filings, earnings context, market data, and issuer references for ${subject}`,
          `Capture source links for performance, valuation, industry conditions, and company-specific drivers for ${subject}`,
          "Identify required risk disclaimers and unsupported claims to avoid"
        ]
      },
      {
        title: withTaskTitle(`Draft JP Morgan style WordPress blog note for ${subject}`, title),
        atomic: [
          "Write executive-summary lead, investment view, key drivers, risks, and conclusion sections",
          "Prepare WordPress title, excerpt, headings, and SEO-friendly metadata",
          "Add factual citations, risk disclaimer language, and no-advice framing"
        ]
      },
      {
        title: withTaskTitle(`Validate, publish handoff, and capture proof for ${subject}`, title),
        atomic: [
          "Run factual, citation, style, and compliance checks against the brief",
          "Create an unpublished WordPress draft and capture its preview for review",
          { title: "Review and approve the WordPress draft before publication", assignee: "member" },
          "Publish only after explicit approval, then capture the WordPress URL and proof summary"
        ]
      }
    ];
  }

  if (isWordpressContentTask(context)) {
    return [
      {
        title: withTaskTitle("Define WordPress blog brief and publish target", title),
        atomic: [
          "Extract topic, audience, style, and publish/draft destination from task context",
          "Define outline, SEO fields, and approval checkpoint before publish",
          "List source links, images, and WordPress access dependencies"
        ]
      },
      {
        title: withTaskTitle("Research and draft complete WordPress post", title),
        atomic: [
          "Gather supporting facts, references, and examples for the blog topic",
          "Write title, excerpt, body sections, and conclusion in requested style",
          "Prepare WordPress-ready formatting, headings, links, and metadata"
        ]
      },
      {
        title: withTaskTitle("Validate, publish handoff, and archive proof", title),
        atomic: [
          "Check factual accuracy, links, formatting, and requested tone",
          "Create WordPress draft or publish after approval as requested",
          "Capture URL, draft/publish status, and handoff summary"
        ]
      }
    ];
  }

  if (isContentGuideTask(context)) {
    return [
      {
        title: withTaskTitle("Define guide brief and structure", title),
        atomic: [
          "Extract objective, audience, and constraints from task context",
          "Define section outline and acceptance criteria",
          "List required references and source materials"
        ]
      },
      {
        title: withTaskTitle("Produce first complete guide draft", title),
        atomic: [
          "Gather required facts, examples, and source links",
          "Write end-to-end draft aligned to requested format",
          "Map each section to definition-of-done checks"
        ]
      },
      {
        title: withTaskTitle("Review, polish, and finalize publication handoff", title),
        atomic: [
          "Validate factual accuracy and remove unsupported claims",
          "Edit for clarity, flow, and consistency",
          "Publish final artifact and provide concise handoff summary"
        ]
      }
    ];
  }

  if (isResearchRankingTask(context)) {
    return [
      {
        title: withTaskTitle("Define ranking criteria and output schema", title),
        atomic: [
          "Extract hard constraints and eligibility rules from task context",
          "Define ranking criteria with weighting",
          "Lock required output fields (including links and citations)"
        ]
      },
      {
        title: withTaskTitle("Research and shortlist valid candidates", title),
        atomic: [
          "Gather candidate options from credible sources",
          "Capture specs, price, and primary links for each candidate",
          "Filter out candidates that fail mandatory constraints"
        ]
      },
      {
        title: withTaskTitle("Validate and deliver final ranked list", title),
        atomic: [
          "Cross-check evidence and remove weak entries",
          "Rank final options with concise rationale",
          "Publish final table in requested format with references"
        ]
      }
    ];
  }

  return [
    {
      title: withTaskTitle("Frame target output and constraints", title),
      atomic: [
        "Extract objective, constraints, and required inputs from task context",
        "Define acceptance criteria and output format",
        "List dependencies, tools, and external references needed"
      ]
    },
    {
      title: withTaskTitle("Create first complete deliverable", title),
      atomic: [
        "Gather and prepare required materials/data/files",
        "Execute core work and produce a full draft output",
        "Capture links/evidence/artifacts used during execution"
      ]
    },
    {
      title: withTaskTitle("Validate, finalize, and hand off", title),
      atomic: [
        "Check deliverable against definition of done and constraints",
        "Fix gaps, edge cases, and quality issues",
        "Publish final output with concise handoff summary"
      ]
    }
  ];
};

export const buildTaskPlanTemplate = ({ title, description, assignee }: PlanningInput): TaskPlanTemplate => {
  const titleSeed = title.trim() || (description?.trim() || "task");
  const contextSeed = [title, description ?? ""].join(" ").trim().toLowerCase();
  const subtasks = attachStatuses(assignee, fallbackPlan(titleSeed, contextSeed));
  return { profile: "general", subtasks };
};
